// Screen: "Earn Bucks" — watch ads to earn Bucks
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import BucksDisplay from '../components/BucksDisplay';
import { claimAdReward, getAdRewardConfig, redeemPromoCode } from '../apiClient';

const AD_WATCH_SECONDS = 15;

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

/**
 * AdWatchModal — shows a Google AdSense ad with a countdown timer.
 * If AdSense is not yet approved or ad fails to load, shows a sponsor
 * placeholder so the page isn't blank.
 */
function AdWatchModal({ onClaim, onClose, isMobile }) {
  const [countdown, setCountdown] = useState(AD_WATCH_SECONDS);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const adContainerRef = useRef(null);
  const adAttempted = useRef(false);

  // Try to push an AdSense ad into the container
  useEffect(() => {
    if (adAttempted.current) return;
    adAttempted.current = true;

    // Small delay to let the DOM render
    const timer = setTimeout(() => {
      try {
        const container = adContainerRef.current;
        if (!container) return;

        // Create the ins element dynamically so each modal gets a fresh ad
        const ins = document.createElement('ins');
        ins.className = 'adsbygoogle';
        ins.style.display = 'block';
        ins.style.width = '100%';
        ins.style.minHeight = '250px';
        ins.setAttribute('data-ad-client', 'ca-pub-2784532590630929');
        ins.setAttribute('data-ad-format', 'auto');
        ins.setAttribute('data-full-width-responsive', 'true');
        container.appendChild(ins);

        (window.adsbygoogle = window.adsbygoogle || []).push({});

        // Check after a delay if the ad filled
        setTimeout(() => {
          // AdSense fills the ins element — if it has child elements or height > 0, ad loaded
          if (ins.querySelector('iframe') || ins.offsetHeight > 60) {
            setAdLoaded(true);
          }
        }, 2000);
      } catch (e) {
        // AdSense not available
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleClaim = async () => {
    setClaiming(true);
    const res = await onClaim();
    setResult(res);
    setClaiming(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16
    }}
      onClick={(e) => { if (e.target === e.currentTarget && result) onClose(); }}
    >
      <div style={{
        background: '#161b22', borderRadius: 12, border: '1px solid #30363d',
        padding: isMobile ? 20 : 32, maxWidth: 500, width: '100%',
        textAlign: 'center', fontFamily: "'Georgia', serif", color: '#e2e8f0'
      }}>
        <h2 style={{ fontSize: isMobile ? 18 : 22, marginBottom: 16, color: '#58a6ff' }}>
          {result ? 'Reward Claimed!' : 'Watch Ad to Earn Bucks'}
        </h2>

        {/* Ad container — AdSense fills this, or fallback shows */}
        {!result && (
          <div style={{
            background: '#0d1117', borderRadius: 8, border: '1px solid #30363d',
            minHeight: 250, marginBottom: 16, overflow: 'hidden',
            position: 'relative'
          }}>
            {/* AdSense ad target */}
            <div ref={adContainerRef} style={{ width: '100%', minHeight: 250 }} />

            {/* Fallback if no ad loaded after 2s */}
            {!adLoaded && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                color: '#484f58', padding: 20, pointerEvents: 'none'
              }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  Advertisement
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, maxWidth: 300 }}>
                  Ad loading... If you don't see an ad, your AdSense account may still be under review by Google (typically 1-3 days).
                </div>
              </div>
            )}
          </div>
        )}

        {result ? (
          <>
            <div style={{
              padding: '12px 16px', borderRadius: 8, fontSize: 18, fontWeight: 700,
              background: '#1c3d2a', border: '1px solid #3fb950', color: '#3fb950',
              marginBottom: 16
            }}>
              +{result.amount} Bucks earned!
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '12px 32px', background: '#238636', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </>
        ) : (
          <>
            {countdown > 0 ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 14, color: '#8b949e', marginBottom: 8 }}>
                  Please watch the ad above
                </div>
                <div style={{
                  display: 'inline-block', background: '#0d1117',
                  border: '1px solid #30363d', borderRadius: 8,
                  padding: '12px 32px', fontSize: 20, fontWeight: 700,
                  color: '#fbbf24', minWidth: 100
                }}>
                  {countdown}s
                </div>
                <div style={{
                  marginTop: 12, height: 4, background: '#21262d', borderRadius: 2, overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%', background: '#238636', borderRadius: 2,
                    width: `${((AD_WATCH_SECONDS - countdown) / AD_WATCH_SECONDS) * 100}%`,
                    transition: 'width 1s linear'
                  }} />
                </div>
              </div>
            ) : (
              <button
                onClick={handleClaim}
                disabled={claiming}
                style={{
                  padding: '14px 40px', background: claiming ? '#4b5563' : '#238636',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: isMobile ? 16 : 18, fontWeight: 700,
                  cursor: claiming ? 'not-allowed' : 'pointer',
                  animation: claiming ? 'none' : 'pulse 1.5s ease-in-out infinite'
                }}
              >
                {claiming ? 'Claiming...' : 'Claim Your Bucks!'}
              </button>
            )}

            <div style={{ marginTop: 12 }}>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent', border: 'none', color: '#484f58',
                  fontSize: 12, cursor: 'pointer', textDecoration: 'underline'
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}

function PromoCodeSection({ token, updateUser, isMobile }) {
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!promoCode.trim() || redeeming) return;
    setRedeeming(true);
    setPromoMsg(null);

    const result = await redeemPromoCode(token, promoCode.trim());
    setRedeeming(false);

    if (result.success) {
      setPromoMsg({ type: 'success', text: result.message || `${result.bucksAwarded} Bucks added!` });
      setPromoCode('');
      if (result.newBalance !== undefined) {
        updateUser({ playMoneyBalance: result.newBalance });
      }
    } else {
      setPromoMsg({ type: 'error', text: result.error || 'Failed to redeem code' });
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: isMobile ? 18 : 20, marginBottom: 12, color: '#58a6ff' }}>Promo Code</h2>
      <form onSubmit={handleRedeem} style={{
        display: 'flex', gap: 10, alignItems: 'center',
        marginBottom: promoMsg ? 10 : 0,
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <input
          type="text"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
          placeholder="Enter promo code"
          style={{
            flex: 1, minWidth: isMobile ? '100%' : 200,
            padding: '12px 16px', background: '#0d1117',
            border: '1px solid #30363d', borderRadius: 6,
            color: '#e2e8f0', fontSize: 15,
            fontFamily: 'monospace', letterSpacing: 2,
            textTransform: 'uppercase'
          }}
        />
        <button
          type="submit"
          disabled={!promoCode.trim() || redeeming}
          style={{
            padding: '12px 24px', background: '#238636',
            color: '#fff', border: 'none', borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: !promoCode.trim() || redeeming ? 'not-allowed' : 'pointer',
            opacity: !promoCode.trim() || redeeming ? 0.5 : 1,
            whiteSpace: 'nowrap'
          }}
        >
          {redeeming ? 'Applying...' : 'Apply Code'}
        </button>
      </form>
      {promoMsg && (
        <div style={{
          padding: '10px 16px', borderRadius: 6, fontSize: 13,
          background: promoMsg.type === 'success' ? '#1c3d2a' : '#3d1c1c',
          border: `1px solid ${promoMsg.type === 'success' ? '#3fb950' : '#f85149'}`,
          color: promoMsg.type === 'success' ? '#3fb950' : '#f85149'
        }}>
          {promoMsg.text}
        </div>
      )}
    </div>
  );
}

export default function FundAccountPage() {
  const { user, token, updateUser, isClubMember } = useAuth();
  const isMobile = useIsMobile();
  const [config, setConfig] = useState({ baseAmount: 15, bonusRange: 11, maxPerDay: 5, remaining: 5 });
  const [showAdModal, setShowAdModal] = useState(false);
  const [adMsg, setAdMsg] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    const cfg = await getAdRewardConfig(token);
    setConfig(cfg);
  }, [token]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleClaimReward = async () => {
    const result = await claimAdReward(token);
    if (result.success) {
      if (result.newBalance !== undefined) {
        updateUser({ playMoneyBalance: result.newBalance });
      }
      setConfig(prev => ({ ...prev, remaining: result.remaining ?? Math.max(0, prev.remaining - 1) }));
      setAdMsg({ type: 'success', text: `+${result.amount} Bucks earned!` });
      return result;
    } else {
      setAdMsg({ type: 'error', text: result.error || 'Failed to claim reward' });
      return null;
    }
  };

  const maxReward = config.baseAmount + config.bonusRange - 1;

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 800, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>
        Earn Bucks
      </h1>

      <div style={{
        background: '#161b22', borderRadius: 12,
        padding: isMobile ? 20 : 32,
        border: '1px solid #30363d', marginBottom: 24
      }}>
        <h2 style={{ fontSize: isMobile ? 20 : 24, marginBottom: 24, color: '#58a6ff' }}>Current Balance</h2>

        <div style={{
          background: '#0d1117', borderRadius: 8,
          padding: isMobile ? 16 : 20,
          border: '1px solid #fbbf2440',
          marginBottom: isMobile ? 24 : 32,
          textAlign: 'center'
        }}>
          <div style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14, marginBottom: 8 }}>Bucks Balance</div>
          <BucksDisplay amount={user?.playMoneyBalance || 100} size="xl" />
        </div>

        {/* Watch Ad Section */}
        <h2 style={{ fontSize: isMobile ? 18 : 20, marginBottom: 12, color: '#58a6ff' }}>Watch Ads for Bucks</h2>
        <p style={{ color: '#8b949e', fontSize: isMobile ? 13 : 14, lineHeight: 1.6, marginBottom: 16 }}>
          Watch a short ad to earn {config.baseAmount}-{maxReward} Bucks. You can watch up to {config.maxPerDay} ads per day.
        </p>

        <div style={{
          background: '#1c2128', borderRadius: 8,
          padding: isMobile ? 20 : 24, marginBottom: 24,
          textAlign: 'center'
        }}>
          {config.remaining > 0 ? (
            <>
              <button
                onClick={() => { setAdMsg(null); setShowAdModal(true); }}
                style={{
                  width: '100%', maxWidth: 360, padding: isMobile ? '16px' : '18px 32px',
                  background: '#238636',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: isMobile ? 16 : 18, fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: 12
                }}
              >
                Watch Ad for +{config.baseAmount}-{maxReward} Bucks
              </button>
              <div style={{ color: '#8b949e', fontSize: 13 }}>
                {config.remaining} of {config.maxPerDay} remaining today
              </div>
            </>
          ) : (
            <div style={{ padding: 20, color: '#8b949e', fontSize: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#fbbf24', marginBottom: 8 }}>
                All ads watched for today!
              </div>
              <p>Come back tomorrow to earn more Bucks.</p>
            </div>
          )}

          {adMsg && (
            <div style={{
              marginTop: 12, padding: '10px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600,
              background: adMsg.type === 'success' ? '#1c3d2a' : '#3d1c1c',
              border: `1px solid ${adMsg.type === 'success' ? '#3fb950' : '#f85149'}`,
              color: adMsg.type === 'success' ? '#3fb950' : '#f85149'
            }}>
              {adMsg.text}
            </div>
          )}
        </div>

        {/* Other ways to earn */}
        <h2 style={{ fontSize: isMobile ? 18 : 20, marginBottom: 12, color: '#58a6ff' }}>Other Ways to Earn</h2>
        <div style={{
          background: '#1c2128', borderRadius: 8,
          padding: isMobile ? 16 : 20, marginBottom: 24
        }}>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#8b949e', fontSize: isMobile ? 13 : 14, lineHeight: 2 }}>
            <li>Daily login: <span style={{ color: '#3fb950', fontWeight: 600 }}>{isClubMember ? '8' : '5'} Bucks/day</span></li>
            <li>First Week Challenges: <span style={{ color: '#3fb950', fontWeight: 600 }}>25 Bucks each</span></li>
            {isClubMember && <li>Monthly Club bonus: <span style={{ color: '#3fb950', fontWeight: 600 }}>50 Bucks</span></li>}
            <li>Win games with wagers to grow your stack</li>
          </ul>
        </div>

        <PromoCodeSection token={token} updateUser={updateUser} isMobile={isMobile} />
      </div>

      {/* Club Membership CTA */}
      {!isClubMember && (
        <div style={{
          background: 'linear-gradient(135deg, #1c3d2a, #161b22)',
          borderRadius: 12, padding: isMobile ? 16 : 24,
          border: '1px solid #3fb95040', textAlign: 'center'
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#3fb950', marginBottom: 8 }}>
            Want more Bucks every day?
          </div>
          <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 16 }}>
            Club Members get 8 Bucks/day + 50 Bucks/month bonus + ad-free play.
          </div>
          <a href="/membership" style={{
            display: 'inline-block', background: '#3fb950', color: '#000',
            padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700,
            textDecoration: 'none'
          }}>
            View Membership Plans
          </a>
        </div>
      )}

      {/* Ad Watch Modal */}
      {showAdModal && (
        <AdWatchModal
          isMobile={isMobile}
          onClaim={handleClaimReward}
          onClose={() => setShowAdModal(false)}
        />
      )}
    </div>
  );
}
